import React, {
  ComponentProps,
  forwardRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

import { getScrollParents } from "@kikoff/client-utils/src/dom";
import { findChild } from "@kikoff/client-utils/src/react";
import useId from "@kikoff/hooks/src/useId";
import useInitial from "@kikoff/hooks/src/useInitial";
import useObjectState from "@kikoff/hooks/src/useObjectState";
import usePrevious from "@kikoff/hooks/src/usePrevious";
import useStateOrBind from "@kikoff/hooks/src/useStateOrBind";
import { clamp } from "@kikoff/utils/src/number";
import { cls, combineClasses } from "@kikoff/utils/src/string";

import check from "./images/check.svg";
import selectArrow from "./images/select_arrow.svg";

import styles from "./Select.module.scss";

type Value = any;

declare namespace Select {
  type Props = {
    className?: string;
    children: React.ReactNode;
    variant?: "default";
    label?: string;
    labelPosition?: "top" | "inline";
    id?: string;
    // Input name accessible from form
    name?: string;
    onChange?(value: Value): void;
    controller?: SelectController;
    disabled?: boolean;

    propsFor?: { menu: ComponentProps<typeof motion.div> };
  } & (
    | {
        // Default index can be useful for defaulting to first option in menu (using 0)
        defaultIndex?: number;
      }
    | {
        defaultValue?: Value;
      }
    | {
        value: Value | null;
      }
  );
}

const Select = forwardRef<HTMLDivElement, Select.Props>(
  (
    {
      className,
      propsFor,
      name,
      variant = "default",
      children,
      label,
      labelPosition = "top",
      id: _id,
      disabled,
      ...props
    },
    ref
  ) => {
    const initial = useInitial();

    const { menuShown, menuRef, showMenu, hideMenu } =
      props.controller || useSelectController();

    const [overTop, setOverTop] = useState(false);
    const [menuBounds, setMenuBounds] = useObjectState({
      top: 0,
      left: 0,
      width: 0,
    });
    const getDefault = () => {
      if ("defaultValue" in props) return props.defaultValue;
      if (!("defaultIndex" in props)) return null;

      return (findChild(
        children,
        // ANCHOR[id=type-key]
        (child) =>
          React.isValidElement(child) &&
          (child.type as any)?.key === styles.option &&
          !child.props.disabled,
        props.defaultIndex
      ) as any)?.props?.value as string;
    };

    const [selected, setSelected] = useStateOrBind(getDefault, [
      "value" in props ? props.value : undefined,
      props.onChange,
    ]);

    const hasSelection = selected != null;

    const selectedLabel = useMemo(
      () =>
        selected != null &&
        findChild(
          children,
          (child) =>
            React.isValidElement(child) && child.props.value === selected
        ),
      [selected]
    );

    const id = useId(_id);
    const inputRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const menu = menuRef.current;
      const input = inputRef.current;

      function click(e) {
        if (menu.contains(e.target)) return;
        hideMenu();
      }
      function updateMenuBounds() {
        const inputBox = input.getBoundingClientRect();
        setMenuBounds({
          top: inputBox.top + inputBox.height,
          left: inputBox.left,
          width: inputBox.width,
        });
      }
      if (menuShown) {
        updateMenuBounds();

        // Close menu when user clicks outside
        requestAnimationFrame(() => {
          document.addEventListener("click", click);
        });
        window.addEventListener("resize", updateMenuBounds);

        // Update menu position on scroll
        const scrollParents = getScrollParents(input);
        for (const parent of scrollParents) {
          parent.addEventListener("scroll", updateMenuBounds);
        }
        return () => {
          document.removeEventListener("click", click);
          window.removeEventListener("resize", updateMenuBounds);
          for (const parent of scrollParents) {
            parent.removeEventListener("scroll", updateMenuBounds);
          }
        };
      }
    }, [menuShown]);

    function preventMenuOverflow() {
      const menu = menuRef.current;
      const input = inputRef.current;

      if (menuShown) {
        const menuBox = menu.getBoundingClientRect();
        const menuOverflow = menuBox.bottom - window.innerHeight;

        if (menuOverflow > 0) {
          setMenuBounds({
            top: menuBounds.top - menuOverflow,
          });
          setOverTop(menuOverflow > input.clientHeight);
        }
      }
    }

    // Using layout effect to prevent flashing, useEffect still necessary for
    // initial calculation since menu position must be up to date
    useLayoutEffect(preventMenuOverflow, [menuBounds]);
    const prevShowMenu = usePrevious(menuShown);
    useEffect(() => {
      // TODO(kcirtaptrick): Reverse css transform, animation currently cuts off
      // part of menu, but doesn't block any options so this is non breaking
      if (menuShown && !prevShowMenu) preventMenuOverflow();
    }, [menuBounds]);

    const { className: menuClassName, ...menuCustomProps } =
      propsFor?.menu ?? {};

    return (
      <div
        data-cy="selector-menu"
        ref={ref}
        className={combineClasses(
          styles["select"],
          styles[variant],
          hasSelection && styles["has-value"],
          menuShown && styles["show-menu"],
          styles[`label-position_${labelPosition}`],
          className
        )}
      >
        {/* For forms that require input with name */}
        <input
          value={selected ?? ""}
          name={name}
          readOnly
          className={styles.native}
        />
        <div
          ref={inputRef}
          className={styles["input-container"]}
          onClick={showMenu}
          onKeyDown={(e) => {
            const values = findChild(
              children,
              (child: any) =>
                child.type?.key === styles.option && !child.props.disabled,
              "all"
            ).map((child) => (child as any).props.value as string);
            const relativeValue = (delta: number) =>
              values[
                clamp(values.indexOf(selected) + delta, {
                  min: 0,
                  max: values.length - 1,
                })
              ];
            const nextValue = relativeValue(
              {
                ArrowUp: -1,
                ArrowDown: 1,
              }[e.key]
            );
            if (nextValue != null) {
              e.preventDefault();
              return setSelected(nextValue);
            }
            ({
              Enter() {
                // Prevent form submittion
                e.preventDefault();
                (menuShown ? hideMenu : showMenu)();
              },
              Escape: hideMenu,
            }[e.key]?.());
          }}
          role="combobox"
          aria-controls={`${id}-listbox`}
          aria-expanded={menuShown}
          tabIndex={0}
        >
          <div className={styles.label}>{label}</div>
          <div
            className={cls(
              styles.selected,
              React.isValidElement(selectedLabel) &&
                selectedLabel.props.subtitle &&
                styles["with-subtitle"]
            )}
          >
            <AnimatePresence>
              {hasSelection && (
                <motion.div
                  key={selected}
                  className={styles["label-wrapper"]}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Prevent selectedLabel context from leaking */}
                  <SelectContext.Provider
                    value={{ ...defaultContext, isPreview: true }}
                  >
                    {selectedLabel}
                  </SelectContext.Provider>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <img src={selectArrow.src} alt="" className={styles.expander} />
        </div>

        {
          // For SSR compatibility, portals are not available on server
          !initial &&
            ReactDOM.createPortal(
              <AnimatePresence>
                {menuShown && (
                  <motion.div
                    ref={menuRef}
                    style={menuBounds}
                    className={combineClasses(
                      styles["menu"],
                      menuClassName,
                      overTop && styles["over-top"]
                    )}
                    initial={{ opacity: 0, translateY: -16 }}
                    animate={{ opacity: 1, translateY: -1 }}
                    exit={{ opacity: 0, translateY: -16 }}
                    transition={{ duration: 0.2 }}
                    {...menuCustomProps}
                  >
                    {/* Options use SelectContext to notify input when they are selected */}
                    <SelectContext.Provider
                      value={{
                        isPreview: false,
                        selected,
                        onSelect(value) {
                          setSelected(value);
                          hideMenu();
                        },
                        hideMenu,
                        showMenu: menuShown,
                      }}
                    >
                      {children}
                    </SelectContext.Provider>
                  </motion.div>
                )}
              </AnimatePresence>,
              document.getElementById("app-root")
            )
        }
      </div>
    );
  }
);

export default Select;

export function useSelectController() {
  const [menuShown, setMenuShown] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  return {
    menuShown,
    menuRef,
    showMenu() {
      setMenuShown(true);
    },
    hideMenu() {
      setMenuShown(false);
    },
    focus() {
      menuRef.current?.focus();
    },
  };
}

export type SelectController = ReturnType<typeof useSelectController>;

export interface OptionContext {
  selected: boolean;
  isPreview: boolean;
}

interface OptionProps {
  className?: string;
  value: Value;
  disabled?: boolean;
  disabledStyle?: "transparent" | "none";
  subtitle?: string;
  children?:
    | React.ReactNode
    | (({ selected }: OptionContext) => React.ReactNode);
}

export function Option({
  className,
  value,
  disabled,
  disabledStyle = "transparent",
  subtitle,
  children,
}: OptionProps) {
  const { selected, onSelect, isPreview } = useContext(SelectContext);

  return (
    <MenuContentContainer
      className={combineClasses(
        styles.option,
        selected === value && styles.selected,
        disabled && disabledStyle === "transparent" && styles.disabled,
        "text:regular",
        "color:strong",
        className
      )}
      role="option"
      aria-selected={false}
      onClick={() => {
        if (disabled) return;
        onSelect(value);
      }}
      data-cy="selector-option"
    >
      <div className={styles.content}>
        <div>
          {typeof children === "function"
            ? children({ selected: selected === value, isPreview })
            : children || value}
        </div>
        {subtitle && <div className="text:mini color:moderate">{subtitle}</div>}
      </div>
      <AnimatePresence>
        {selected === value && (
          <motion.img
            src={check.src}
            style={{ marginLeft: "auto" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
            alt=""
          />
        )}
      </AnimatePresence>
    </MenuContentContainer>
  );
}
// Used to identify component in children, loses reference during dev refresh so
// using a string instead, once this component is stable, change this to check
// for Option: LINK #type-key
Option.key = styles.option;

interface OptGroupProps {
  label?:
    | React.ReactNode
    | ((
        context: typeof defaultContext & { isEmpty: boolean }
      ) => React.ReactNode);
  children: React.ReactNode;
}

export function OptGroup({ label, children }: OptGroupProps) {
  const childArray = [children].flat().filter(Boolean);

  const context = useContext(SelectContext);

  return (
    <div className={`${styles["opt-group"]} text:small color:moderate-weak`}>
      {label && (
        <header>
          {typeof label === "function"
            ? label({ ...context, isEmpty: childArray.length === 0 })
            : label}
        </header>
      )}
      <div className={styles.options}>{children}</div>
    </div>
  );
}

const defaultContext = {
  selected: null as string,
  onSelect(value: Value) {},
  hideMenu() {},
  isPreview: false,
  showMenu: false,
};

const SelectContext = React.createContext(defaultContext);
export const useSelectContext = () => useContext(SelectContext);

type MenuContentContainerProps = JSX.IntrinsicElements["div"];

export function MenuContentContainer({
  children,
  className,
  ...props
}: MenuContentContainerProps) {
  return (
    <div
      className={combineClasses(styles["menu-content-container"], className)}
      {...props}
    >
      {children}
    </div>
  );
}
